/**
 * AttachmentPicker
 *
 * Controlled picker for adding image/document attachments to an event before
 * it's created. Uploads via attachmentUploadService (school + domain scoped),
 * shows per-file progress, and emits the growing AttachmentRecord array via
 * onChange. Domain-agnostic — reused across every domain's create flow.
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import {
  CameraIcon,
  ChevronDownIcon,
  DocumentIcon,
  PaperClipIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import {
  uploadAttachment,
  removeAttachment,
  newSessionId,
} from '../../services/attachments/attachmentUploadService';
import {
  ATTACHMENT_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ACCEPT,
} from '../../services/photoStorageService';
import AttachmentList from './AttachmentList';
import AttachmentComposerTray from './AttachmentComposerTray';

const DEFAULT_ACCEPT = ATTACHMENT_UPLOAD_ACCEPT;
const DOCUMENT_UPLOAD_ACCEPT = 'application/pdf,.doc,.docx';

export default function AttachmentPicker({
  schoolId,
  domain,
  currentUser,
  value = [],
  onChange,
  maxFiles = 10,
  maxConcurrentUploads = 3,
  maxSizeMb = 10,
  accept = DEFAULT_ACCEPT,
  label = 'Add photos or documents',
  showLimits = true,
  buttonClassName = 'btn-sm',
  icon: Icon = PaperClipIcon,
  layout = 'stacked',
  buttonSlotClassName = 'col-start-2 row-start-2 self-center',
  contentSlotClassName = 'col-span-3 row-start-1',
  showCamera = false,
  disabled = false,
  deleteOnRemove = true,
  uploadHandler = uploadAttachment,
  removeHandler = removeAttachment,
  onUploaded,
  onUploadingChange,
}) {
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const valueRef = useRef(value);
  const previewUrlsRef = useRef(new Map());
  // One session id per picker instance groups this event's uploads in Storage.
  const sessionId = useMemo(() => newSessionId(), []);
  const [uploading, setUploading] = useState([]); // [{ id, fileName, progress, status, previewUrl }]
  const [failed, setFailed] = useState([]); // [{ id, file, message }]
  const [localPreviews, setLocalPreviews] = useState({});
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerAccept, setPickerAccept] = useState(accept);

  const atLimit = value.length + uploading.length >= maxFiles;
  const usesActionGrid = layout === 'action-grid';

  const openPicker = (nextAccept) => {
    setPickerAccept(nextAccept);
    setMenuOpen(false);
    window.requestAnimationFrame(() => inputRef.current?.click());
  };

  const openCamera = () => {
    setMenuOpen(false);
    window.requestAnimationFrame(() => cameraInputRef.current?.click());
  };

  useEffect(() => {
    onUploadingChange?.(uploading.length > 0);
  }, [onUploadingChange, uploading.length]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => () => {
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  useEffect(() => {
    if (error && /permission|unauthori[sz]ed|storage\/unauthorized/i.test(error)) {
      setError(null);
    }
  }, [error]);

  const uploadOne = async (file, queuedId = null) => {
    const tempId = queuedId || `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 6)}`;
    if (!queuedId) {
      const previewUrl = file.type.startsWith('image/') && typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(file)
        : null;
      if (previewUrl) previewUrlsRef.current.set(tempId, previewUrl);
      setUploading((items) => [...items, {
        id: tempId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        kind: file.type.startsWith('image/') ? 'image' : 'document',
        previewUrl,
        progress: 0,
        status: 'uploading',
      }]);
    } else {
      setUploading(items => items.map(item => (
        item.id === tempId ? { ...item, status: 'uploading' } : item
      )));
    }
    try {
      const record = await uploadHandler(file, {
        schoolId,
        domain,
        sessionId,
        uploadedBy: currentUser?.uid,
        uploadedByName: currentUser?.displayName || currentUser?.email,
        onProgress: (progress) => setUploading(items => (
          items.map(item => item.id === tempId ? { ...item, progress } : item)
        )),
      });
      const previewUrl = previewUrlsRef.current.get(tempId);
      if (previewUrl) {
        previewUrlsRef.current.delete(tempId);
        previewUrlsRef.current.set(record.id, previewUrl);
        setLocalPreviews(previews => ({ ...previews, [record.id]: previewUrl }));
      }
      const next = [...valueRef.current, record];
      valueRef.current = next;
      onUploaded?.(record);
      onChange?.(next);
      return record;
    } catch (uploadError) {
      console.error('Attachment upload failed:', uploadError);
      setFailed(items => [
        ...items.filter(item => item.id !== tempId),
        { id: tempId, file, message: uploadError.message || 'Upload failed' },
      ]);
      return null;
    } finally {
      setUploading(items => items.filter(item => item.id !== tempId));
      const previewUrl = previewUrlsRef.current.get(tempId);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrlsRef.current.delete(tempId);
      }
    }
  };

  const handleFiles = async (fileList) => {
    setError(null);
    const files = Array.from(fileList || []);
    const remaining = maxFiles - value.length - uploading.length;
    const toUpload = files.slice(0, Math.max(0, remaining));
    const oversize = toUpload.find(file => file.size > maxSizeMb * 1024 * 1024);
    if (oversize) {
      setError(`${oversize.name} is larger than ${maxSizeMb}MB.`);
      return;
    }
    if (files.length > toUpload.length) {
      setError(`Choose up to ${maxFiles} files.`);
    }

    const queuedFiles = toUpload.map((file, index) => {
      const id = `${Date.now()}-${index}-${file.name}-${Math.random().toString(36).slice(2, 6)}`;
      const previewUrl = file.type.startsWith('image/') && typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(file)
        : null;
      if (previewUrl) previewUrlsRef.current.set(id, previewUrl);
      return { file, id, previewUrl };
    });
    setUploading((items) => [
      ...items,
      ...queuedFiles.map(({ file, id, previewUrl }) => ({
        id,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        kind: file.type.startsWith('image/') ? 'image' : 'document',
        previewUrl,
        progress: 0,
        status: 'waiting',
      })),
    ]);

    let cursor = 0;
    const uploadNext = async () => {
      while (cursor < queuedFiles.length) {
        const nextIndex = cursor;
        cursor += 1;
        const { file, id } = queuedFiles[nextIndex];
        await uploadOne(file, id);
      }
    };
    const workerCount = Math.min(
      Math.max(1, maxConcurrentUploads),
      queuedFiles.length,
    );
    await Promise.all(Array.from({ length: workerCount }, uploadNext));
  };

  const handleRemove = async (id) => {
    if (disabled) return;
    const attachment = value.find(item => item.id === id);
    setError(null);
    onChange?.(value.filter(item => item.id !== id));
    const previewUrl = previewUrlsRef.current.get(id);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrlsRef.current.delete(id);
      setLocalPreviews(previews => {
        const next = { ...previews };
        delete next[id];
        return next;
      });
    }
    if (deleteOnRemove && attachment) {
      try {
        await removeHandler(attachment);
      } catch (removeError) {
        console.warn(
          '[AttachmentPicker] Removed from the draft, but remote attachment cleanup failed.',
          removeError,
        );
      }
    }
  };

  const retry = async (failedUpload) => {
    if (disabled) return;
    setFailed(items => items.filter(item => item.id !== failedUpload.id));
    await uploadOne(failedUpload.file);
  };

  const handlePreviewResolved = (id) => {
    const previewUrl = previewUrlsRef.current.get(id);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrlsRef.current.delete(id);
    }
    setLocalPreviews(previews => {
      if (!previews[id]) return previews;
      const next = { ...previews };
      delete next[id];
      return next;
    });
  };

  return (
    <div className={usesActionGrid ? 'contents' : 'flex flex-col gap-3'}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={pickerAccept}
        disabled={disabled}
        className="hidden"
        data-attachment-menu-input
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = ''; // allow re-selecting the same file
        }}
      />
      {showCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={disabled}
          className="hidden"
          data-attachment-camera-input
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      )}

      <div className={usesActionGrid ? `relative ${buttonSlotClassName}` : 'relative self-start'}>
        <button
          type="button"
          className={`btn btn-outline gap-2 ${buttonClassName}`}
          onClick={() => setMenuOpen((isOpen) => !isOpen)}
          disabled={atLimit || disabled}
          aria-describedby={showLimits ? `${sessionId}-limits` : undefined}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Icon className="w-4 h-4" />
          {label}
          <ChevronDownIcon className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        </button>

        {menuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-10 cursor-default"
              onClick={() => setMenuOpen(false)}
              aria-label="Close attachment menu"
            />
            <div
              className="absolute bottom-full right-0 z-20 mb-2 min-w-40 rounded-lg border border-base-300 bg-base-100 py-2 shadow-xl"
              role="menu"
            >
              {showCamera && (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-base-200"
                  onClick={openCamera}
                  role="menuitem"
                >
                  <CameraIcon className="h-4 w-4 text-base-content" />
                  Take photo
                </button>
              )}
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-base-200"
                onClick={() => openPicker(IMAGE_UPLOAD_ACCEPT)}
                role="menuitem"
              >
                <PhotoIcon className="h-4 w-4 text-base-content" />
                {showCamera ? 'Photo library' : 'Photo'}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-base-200"
                onClick={() => openPicker(DOCUMENT_UPLOAD_ACCEPT)}
                role="menuitem"
              >
                <DocumentIcon className="h-4 w-4 text-base-content" />
                File
              </button>
            </div>
          </>
        )}
      </div>

      <div className={usesActionGrid
        ? `${contentSlotClassName} flex w-full min-w-0 flex-col gap-3 overflow-hidden`
        : 'flex min-w-0 flex-col gap-3'}>
        {showLimits && (
          <p id={`${sessionId}-limits`} className="text-xs text-base-content/60">
            Up to {maxFiles} {maxFiles === 1 ? 'file' : 'files'}, {maxSizeMb}MB each.
          </p>
        )}

        {error && <p className="text-sm text-error" role="alert">{error}</p>}

        {usesActionGrid ? (
          <AttachmentComposerTray
            attachments={value}
            pending={uploading}
            localPreviews={localPreviews}
            onPreviewResolved={handlePreviewResolved}
            onRemove={handleRemove}
            disabled={disabled}
          />
        ) : (
          <AttachmentList
            attachments={value}
            readOnly={false}
            onRemove={handleRemove}
            disabled={disabled}
          />
        )}

        {!usesActionGrid && uploading.map((it) => (
          <div
            key={it.id}
            className="relative flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-base-300 bg-base-100 px-3 py-2.5"
          >
            <PaperClipIcon className="h-4 w-4 shrink-0 text-base-content/50" />
            <span className="min-w-0 flex-1 truncate text-sm">{it.fileName}</span>
            <span className="shrink-0 text-xs tabular-nums text-base-content/50">
              {it.status === 'waiting'
                ? 'Waiting'
                : it.progress > 0
                  ? `${it.progress}%`
                  : 'Starting'}
            </span>
            <progress
              className="progress progress-primary absolute inset-x-0 bottom-0 h-1 w-full rounded-none"
              value={it.progress}
              max="100"
              aria-label={`Uploading ${it.fileName}: ${it.progress}%`}
            />
          </div>
        ))}

        {failed.map(item => (
          <div key={item.id} className="flex items-center gap-3 rounded-lg bg-error/10 p-3 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{item.file.name}</div>
              <div className="text-xs text-error">{item.message}</div>
            </div>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => retry(item)} disabled={disabled}>
              Retry
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setFailed(items => items.filter(failedItem => failedItem.id !== item.id))}
            >
              Remove
            </button>
          </div>
        ))}

        {atLimit && (
          <p className="text-xs text-base-content/60">Maximum {maxFiles} attachments.</p>
        )}
      </div>
    </div>
  );
}
